import type Big from 'big.js';

import { toBig } from './number';

const RADIAN = Math.PI / 180;

type PolarToCartesianArgs = {
  cx: number;
  cy: number;
  radius: number;
  angle: number;
};

export const polarToCartesian = ({
  cx,
  cy,
  radius,
  angle,
}: PolarToCartesianArgs): { x: number; y: number } => ({
  x: cx + Math.cos(-RADIAN * angle) * radius,
  y: cy + Math.sin(-RADIAN * angle) * radius,
});

type LinearMapArgs = {
  value: number;
  sFrom: number;
  sTo: number;
  dFrom: number;
  dTo: number;
};

export const linearMap = ({
  value,
  sFrom,
  sTo,
  dFrom,
  dTo,
}: LinearMapArgs): number => {
  // Y = (X-A)/(B-A) * (D-C) + C
  return ((value - sFrom) / (sTo - sFrom)) * (dTo - dFrom) + dFrom;
};

export const toRadians = (degrees: number): number => degrees * RADIAN;

type Point = [x: number, y: number];

export const angleOfLine = (start: Point, end: Point): number => {
  const [x, y] = [end[0] - start[0], end[1] - start[1]];
  return Math.atan2(y, x);
};

export const pointAtAngleAndDistance = (
  startPoint: Point,
  /** angle in radians */
  angle: number,
  distance: number
): Point => {
  const x = startPoint[0] + distance * Math.cos(angle);
  const y = startPoint[1] + distance * Math.sin(angle);
  return [x, y];
};
/**
 * Calculate the mean of an array of items using Big.js for precise arithmetic
 * @param array Array of items to calculate mean from
 * @param iteratee Function to extract numeric value from each item
 * @returns The mean
 */
export const bigMeanBy = <T>(
  array: T[],
  iteratee: (item: T) => number | null | undefined
): Big => {
  if (array.length === 0) {
    return toBig(0);
  }

  const sum = array.reduce((acc, item) => {
    const value = iteratee(item);
    return acc.add(toBig(value));
  }, toBig(0));

  return sum.div(array.length);
};
